// The sprite index: what the keyword lookup searches, on the client (world.sprite in draw) and
// on the server (GET /api/sprites). Built by tools/sprites/build.ts from the CC0 packs in the
// corpus folder into runtime/shared/sprite-index.ts and public/sprites/*.png. Isomorphic: no
// DOM, no Node.

export type SpriteStyle = "pixel-16" | "pixel-18" | "pixel-24" | "vector";

export interface SpritePack {
  id: string;
  title: string;
  style: SpriteStyle;
  licence: string;
  source: string;
  /** Words every sprite in the pack answers to (e.g. "space ship" for the shooter pack). */
  tags: string;
}

export interface SpriteEntry {
  /** Unique within the pack; kebab-case. */
  name: string;
  /** Space-separated search words. */
  tags: string;
  pack: string;
  /** File under /sprites/. */
  sheet: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface SpriteIndex {
  packs: SpritePack[];
  sprites: SpriteEntry[];
}

export interface SpriteQuery {
  /** A style, or a prefix of one ("pixel" matches every pixel style). */
  style?: string;
  pack?: string;
  limit?: number;
}

const tokens = (s: string): string[] => s.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);

/**
 * Rank sprites for a keyword query. Deterministic: the same query over the same index always
 * gives the same order, so a game that asks for "knight" gets the same knight every time.
 * Scoring: the whole name 10; a query word equal to a name word 3, equal to a tag 2, a prefix of
 * a name word 1.5, a prefix of a tag 1. Ties break on pack order, then name.
 */
export function searchSprites(index: SpriteIndex, query: string, opts: SpriteQuery = {}): SpriteEntry[] {
  const words = tokens(query);
  if (words.length === 0) return [];
  const packOrder = new Map(index.packs.map((p, i) => [p.id, i]));
  const packById = new Map(index.packs.map((p) => [p.id, p]));
  const q = words.join("-");
  const scored: { e: SpriteEntry; s: number }[] = [];
  for (const e of index.sprites) {
    const pack = packById.get(e.pack);
    if (!pack) continue;
    if (opts.pack && e.pack !== opts.pack) continue;
    if (opts.style && !pack.style.startsWith(opts.style)) continue;
    const nameWords = e.name.split("-");
    const tagWords = tokens(e.tags + " " + pack.tags);
    let s = e.name === q ? 10 : 0;
    for (const w of words) {
      if (nameWords.includes(w)) s += 3;
      else if (tagWords.includes(w)) s += 2;
      else if (nameWords.some((n) => n.startsWith(w))) s += 1.5;
      else if (tagWords.some((t) => t.startsWith(w))) s += 1;
    }
    if (s > 0) scored.push({ e, s });
  }
  scored.sort((a, b) => b.s - a.s || packOrder.get(a.e.pack)! - packOrder.get(b.e.pack)! || (a.e.name < b.e.name ? -1 : 1));
  return scored.slice(0, opts.limit ?? 20).map((x) => x.e);
}

/** The pack a sprite belongs to. */
export function packOf(index: SpriteIndex, e: SpriteEntry): SpritePack | undefined {
  return index.packs.find((p) => p.id === e.pack);
}
